<template>
  <div
    v-if="selectedDocumentIds?.length && agreementType"
    class="d-flex justify-space-between align-center mb-3">
    <div>Обрано документів: {{ selectedDocumentIds.length }}</div>

    <div class="d-flex ga-3">
      <v-btn
        :loading="$loading.isLoading('documentRouteStageParticipantSetResult')"
        density="comfortable"
        variant="outlined"
        @click="agree(2)">
        Відхилити
      </v-btn>
      <v-btn
        :loading="$loading.isLoading('documentRouteStageParticipantSetResult')"
        density="comfortable"
        @click="agree(1)">
        {{ agreementType === 'signature' ? 'Підписати' : 'Погодити' }}
      </v-btn>
    </div>
  </div>
  <table-column-picker
    v-slot="{ headers, activatorProps }"
    :headers="docTypeDocumentListHeaders"
    storage-key="documents-table"
    title="Налаштування відображення переліку документів">
    <b-server-table
      v-model="selectedDocumentIds"
      :show-select="!!agreementType"
      :filter-props="{
        filterMap: [
          { key: 'search' },
          { key: 'registrationNumber', component: 'input' },
          { key: 'registrationDate', component: 'input-date' },
          {
            key: 'docTypeDefinitionId',
            component: 'select',
            directory: 'docTypeDefinitions',
          },
          {
            key: 'docTypeClassId',
            component: 'select',
            directory: 'docTypeClasses',
          },
          {
            key: 'departmentId',
            component: 'autocomplete',
            directory: 'activeInternalDepartments',
          },
          {
            key: 'executorId',
            component: 'autocomplete',
            directory: 'userList',
          },
          {
            key: 'registrationJournalId',
            component: 'select',
            directory: 'registrationJournals',
          },
          {
            key: 'created_at',
            dateRangeKeys: ['created_from', 'created_to'],
            component: 'input-date-range',
          },
        ],
      }"
      :headers="headers"
      :request-func="requestFunc"
      @click:row="navigateToDocument($event.uuid)">
      <template #action-button>
        <v-btn
          v-tooltip="'Завантажити реєстр документів'"
          icon="mdi-file-chart-outline"
          border
          color="white"
          density="comfortable"
          @click="downloadPrintedForm"></v-btn>
        <table-column-picker-button v-bind="activatorProps" />
      </template>
      <template #bottom></template>
      <template #item.status="{ item }">
        <v-chip :color="getStatusColor(item.status)">
          <span class="font-weight-bold">{{
            keyToLabel(item.status, 'documentStatus')
          }}</span>
        </v-chip>
      </template>
      <template #item.registrationNumber="{ item }">
        {{ item.registrationNumber || '---' }}
      </template>
      <template #item.definition="{ item }">
        {{ item.definition?.name || '---' }}
      </template>
      <template #item.docTypeClass="{ item }">
        {{ item.docTypeClass?.name || '---' }}
      </template>
      <template #item.registrationDate="{ item }">
        {{
          item.registrationDate
            ? new CustomDate(item.registrationDate).toString()
            : '---'
        }}
      </template>
      <template #item.department="{ item }">
        <v-tooltip :text="item.executor?.department?.name">
          <template #activator="{ props }">
            <div class="text-truncate" style="max-width: 160px" v-bind="props">
              {{ item.executor?.department?.name || '---' }}
            </div>
          </template>
        </v-tooltip>
      </template>
      <template #item.executor="{ item }">
        {{ getPersonName(item.executor, { initials: true }) || '---' }}
      </template>
      <template #item.journal="{ item }">
        <v-tooltip :text="item.journal?.name">
          <template #activator="{ props }">
            <div class="text-truncate" style="max-width: 180px" v-bind="props">
              {{ item.journal?.name || '---' }}
            </div>
          </template>
        </v-tooltip>
      </template>
      <template #item.created_at="{ item }">
        {{
          item.created_at ? new CustomDate(item.created_at).toString() : '---'
        }}
      </template>
      <template #item.actions="{ item }">
        <b-action-menu
          :actions="[
            {
              title: 'Переглянути',
              action: () => navigateToDocument(item.uuid),
              icon: 'mdi-eye',
            },
            {
              title: 'Видалити',
              action: () => deleteItem(item),
              icon: 'mdi-delete-forever',
              disabled: item.status !== 'draft',
            },
          ]">
        </b-action-menu>
      </template>
    </b-server-table>
  </table-column-picker>
</template>

<script lang="ts">
import { computed, inject, ref, shallowRef } from 'vue'
import { docTypeDocumentDelete } from '@/requests/documentType'
import { CustomDate, getPersonName, Query } from 'best-modules/utils'
import { confirm } from 'best-modules/plugins'
import { BActionMenu, BServerTable } from 'best-modules/components/index'
import {
  documentList as documentListRequest,
  printedFormDocument,
} from '@/requests/document'
import { docTypeDocumentListHeaders } from '@/utils/tableHeaders'
import TableColumnPicker from '@/components/TableColumnPicker.vue'
import TableColumnPickerButton from '@/components/buttons/TableColumnPickerButton.vue'
import { useRoute, useRouter } from 'vue-router'
import { keyToLabel } from '@/utils/key-to-label'
import { getStatusColor } from '@/pages/document/helpers'
import { DocumentListItem } from '@/utils/types/document'
import { useParticipantResult } from './participantResult'
import { rerenderKey } from '@/injectionKeys'

export default {
  name: 'DocTypeDocumentList',
  components: {
    TableColumnPickerButton,
    TableColumnPicker,
    BActionMenu,
    BServerTable,
  },
  data() {
    return {
      docTypeDocumentListHeaders,
    }
  },
  methods: { keyToLabel, getPersonName, CustomDate, getStatusColor },
  setup() {
    const route = useRoute()
    const router = useRouter()
    const rerender = inject(rerenderKey, () => void [0])

    const documentList = shallowRef<DocumentListItem[]>([])
    const selectedDocumentIds = ref<number[]>([])
    const selectedDocuments = computed<DocumentListItem[]>(() => {
      return documentList.value.filter(d => {
        return selectedDocumentIds.value.includes(d.id)
      })
    })

    const getQuery = (): Query => {
      switch (route.name) {
        case 'document-list-draft':
          return { status: 'draft' }
        case 'document-list-in-progress':
          return { status: 'in_progress' }
        case 'document-list-revision':
          return { status: 'revision' }
        case 'document-list-on-registration':
          return { status: 'on_registration' }
        case 'document-list-registered':
          return { status: 'registered' }
        case 'document-list-signature-today':
          return { signingDeadline: 'today' }
        case 'document-list-signature-terminated':
          return { signingDeadline: 'overdue' }
        case 'document-list-signature-terminating':
          return { signingDeadline: 'approaching' }
        case 'document-list-agreement-today':
          return { approvalDeadline: 'today' }
        case 'document-list-agreement-terminated':
          return { approvalDeadline: 'overdue' }
        case 'document-list-agreement-terminating':
          return { approvalDeadline: 'approaching' }
        case 'document-list-acceptance':
          return { isAcceptedAcknowledgment: false }
        case 'document-list-accepted':
          return { isAcceptedAcknowledgment: true }
        case 'document-list-review':
          return { isReview: true }
        case 'document-list-me':
          return { isMe: true }
        case 'document-list-incoming':
          return { isMe: true }
        default:
          return {}
      }
    }

    const agreementType = computed<'agreement' | 'signature' | null>(() => {
      switch (route.name) {
        case 'document-list-signature-today':
        case 'document-list-signature-terminated':
        case 'document-list-signature-terminating':
          return 'signature'
        case 'document-list-agreement-today':
        case 'document-list-agreement-terminated':
        case 'document-list-agreement-terminating':
          return 'agreement'
        default:
          return null
      }
    })

    const { agree } = useParticipantResult(
      agreementType.value,
      selectedDocuments
    )
    const requestFunc = (query: any) => {
      return documentListRequest({
        ...query,
        ...getQuery(),
      }).then(res => {
        documentList.value = res.data

        return res
      })
    }

    const deleteItem = (item: any) => {
      confirm({
        text: `Ви впевнені, що хочете видалити документ "${item.title}"?`,
      }).then(() => {
        return docTypeDocumentDelete(item.id).then(rerender)
      })
    }

    const navigateToDocument = (uuid: string) => {
      let routeName
      if (
        [
          'document-list-draft',
          'document-list-in-progress',
          'document-list-revision',
          'document-list-signature-today',
          'document-list-signature-terminated',
          'document-list-signature-terminating',
          'document-list-agreement-today',
          'document-list-agreement-terminated',
          'document-list-agreement-terminating',
          'document-list-acceptance',
          'document-list-accepted',
          'document-list-me',
          'document-list-incoming',
        ].includes(route.name)
      ) {
        routeName = 'document-single'
      }
      if (
        ['document-list-on-registration', 'document-list-registered'].includes(
          route.name
        )
      ) {
        routeName = 'document-registration-single'
      }
      if ('document-list-review' === route.name) {
        routeName = 'document-review-single'
      }

      return router.push({ name: routeName, params: { uuid } })
    }

    const downloadPrintedForm = () => {
      return printedFormDocument({
        ...getQuery(),
        ...(route.query as Query),
      }).then(res => {
        window.open(res.path, '_blank')
      })
    }

    return {
      route,
      deleteItem,
      requestFunc,
      selectedDocumentIds,
      navigateToDocument,
      downloadPrintedForm,
      agreementType,
      agree,
    }
  },
}
</script>
